// Routing contract for voice-captured reminder/task intents.
//
// A "correction" ("no, make that 5pm", "change it to Thursday") must go to
// POST /reminders/resolve-edit so the server updates the existing reminder
// instead of creating a duplicate. That routing requires BOTH is_correction
// and correction_target — if either is missing we fall back to a plain create.
// VoiceModal delegates here so tests can lock this behavior down.

export interface VoiceIntentData {
  intent: string;
  extracted_task?: string;
  suggested_due_date?: string;
  priority?: string;
  is_correction?: boolean;
  correction_target?: string;
  [key: string]: any;
}

interface ApiLike {
  post: (url: string, body: any) => Promise<{ data?: any }>;
}

export interface RoutingResult {
  route: 'resolve-edit' | 'create';
  message: string;
}

function parseDueAt(suggested?: string): string | undefined {
  if (!suggested) return undefined;
  try {
    const d = new Date(suggested);
    if (isNaN(d.getTime())) return undefined;
    return d.toISOString();
  } catch {
    return undefined;
  }
}

export function isCorrectionEdit(data: VoiceIntentData): boolean {
  return Boolean(data.is_correction && data.correction_target);
}

// Multi-item capture (/capture/extract → CaptureConfirmCards → /capture/save-all)
// does blind inserts and has no concept of is_correction/correction_target. If a
// correction utterance ("no, make that 5pm", "change the dentist reminder to
// Friday") lands there, save-all would create a DUPLICATE instead of editing.
// So both VoiceModal and FloatingMicButton must bypass the multi-capture path
// for correction-looking utterances and send them through detect-intent, where
// executeReminderOrTaskIntent routes them to /reminders/resolve-edit.
//
// False positives are safe: the single-intent path still creates normally when
// detect-intent says is_correction=false — the only cost is skipping the
// multi-capture UI. False negatives are what create duplicates, so the pattern
// leans inclusive.
//
// DECISION (multilingual coverage): the guard stays client-side (no extra
// model hop) but is extended beyond English with Arabic patterns — the user's
// locale is Dubai, so Arabic dictation is the realistic second language.
// JS \b only works against ASCII \w, so Arabic alternatives use (^|\s)
// boundaries instead. If another language shows up in voice_history later,
// add its correction markers here with the same boundary technique.
const CORRECTION_UTTERANCE = new RegExp(
  [
    // ── English ─────────────────────────────────────────────────────────
    // leading "no," / "no wait" / "wait no"
    String.raw`^\s*(no|wait)[,\s]`,
    // "actually ..." / "... instead"
    String.raw`\bactually\b`,
    String.raw`\binstead\b`,
    // "make that/it 5pm"
    String.raw`\bmake\s+(that|it)\b`,
    // correction verbs aimed at an existing item
    String.raw`\b(change|move|reschedule|rename|update|correct|edit|fix)\b[^.!?]*\b(that|it|the\s+\w+\s+)?(reminder|task)s?\b`,
    String.raw`\b(change|move|reschedule|push|postpone)\s+(that|it)\b`,
    // "I meant ..." / "I said ..."
    String.raw`\bi\s+(meant|said)\b`,
    // ── Arabic ──────────────────────────────────────────────────────────
    // leading "لا" / "لأ" (no) — "لا، خليها الساعة خمسة"
    String.raw`^\s*(لا|لأ)[،,\s]`,
    // "استنى/انتظر" (wait) at the start
    String.raw`^\s*(استنى|استنا|انتظر)[،,\s]`,
    // "خليها/خليه/اجعلها/اجعله" (make it/that)
    String.raw`(^|\s)(خليها|خليه|خلّيها|خلّيه|اجعلها|اجعله)(\s|$)`,
    // correction verbs: change/edit/move/postpone — "غير التذكير" / "عدل المهمة" / "أجل الموعد"
    String.raw`(^|\s)(غير|غيّر|عدل|عدّل|بدل|بدّل|أجل|أجّل|صحح|صحّح)(\s|$)`,
    // "قصدت" (I meant) / "أنا قلت" (I said)
    String.raw`(^|\s)قصدت(\s|$)`,
    String.raw`(^|\s)أنا\s+قلت(\s|$)`,
    // "بدلاً من / بدلا من" (instead of)
    String.raw`(^|\s)(بدلاً|بدلا)\s+من(\s|$)`,
  ].join('|'),
  'i',
);

export function isLikelyCorrectionUtterance(text: string): boolean {
  if (!text) return false;
  return CORRECTION_UTTERANCE.test(text);
}

// Handles set_reminder / delegate_reminder / create_task. Returns which route
// was taken plus the user-facing confirmation message.
export async function executeReminderOrTaskIntent(
  api: ApiLike,
  data: VoiceIntentData,
  transcript: string,
): Promise<RoutingResult> {
  const isTask = data.intent === 'create_task';
  const due_at = parseDueAt(data.suggested_due_date);
  const title = data.extracted_task || transcript;
  const priority = data.priority || 'medium';

  if (isCorrectionEdit(data)) {
    const { data: r } = await api.post('/reminders/resolve-edit', {
      match: data.correction_target,
      title,
      due_at,
      ...(isTask ? { category: 'executive' } : {}),
      priority,
    });
    const updated = r?.action === 'updated';
    return {
      route: 'resolve-edit',
      message: isTask
        ? (updated ? 'Task updated!' : 'Task created!')
        : (updated ? 'Reminder updated!' : 'Reminder set!'),
    };
  }

  await api.post('/reminders', {
    title,
    due_at,
    category: isTask ? 'executive' : 'general',
    priority,
    source: 'voice',
  });
  return { route: 'create', message: isTask ? 'Task created!' : 'Reminder set!' };
}

// Result of a correction aimed at a non-reminder capture (expense/note).
// 'ambiguous' carries the candidate rows so the UI can ask which record to
// fix; the caller then re-posts with target_id. A correction NEVER creates.
export interface CaptureCorrectionResult {
  route: 'resolve-edit';
  action: 'updated' | 'unchanged' | 'ambiguous' | 'not_found';
  message: string;
  candidates?: any[];
}

export interface ExpenseCorrectionFields {
  amount?: string | number;
  category?: string;
  merchant?: string;
  payment_method?: string;
  target_id?: string;
}

// Correction aimed at an expense ("no, the dinner was 500 not 450") must go to
// POST /expenses/resolve-edit so the original row is amended, never duplicated.
// Only call when isCorrectionEdit(data) is true (or with target_id after the
// user picked a candidate).
export async function executeExpenseCorrection(
  api: ApiLike,
  data: VoiceIntentData,
  draft: ExpenseCorrectionFields,
): Promise<CaptureCorrectionResult> {
  const { data: r } = await api.post('/expenses/resolve-edit', {
    match: data.correction_target,
    ...(draft.target_id ? { target_id: draft.target_id } : {}),
    amount: draft.amount !== undefined && draft.amount !== '' ? Number(draft.amount) : undefined,
    category: draft.category || undefined,
    merchant: draft.merchant || undefined,
    payment_method: draft.payment_method || undefined,
  });
  const action = r?.action || 'not_found';
  const message = action === 'updated' ? 'Expense updated!'
    : r?.message || 'No matching expense found — nothing was changed.';
  return { route: 'resolve-edit', action, message, candidates: r?.candidates };
}

export interface WorkoutCorrectionFields {
  duration_minutes?: number | string | null;
  intensity?: string | null;
  activity_type?: string | null;
  target_id?: string;
}

// Correction aimed at a workout ("actually that run was 45 minutes not 30")
// goes to POST /fitness/logs/resolve-edit so the original log is amended,
// never duplicated. Only call when isCorrectionEdit(data) is true (or with
// target_id after the user picked a candidate).
export async function executeWorkoutCorrection(
  api: ApiLike,
  data: VoiceIntentData,
  fields: WorkoutCorrectionFields,
): Promise<CaptureCorrectionResult> {
  const { data: r } = await api.post('/fitness/logs/resolve-edit', {
    match: data.correction_target,
    ...(fields.target_id ? { target_id: fields.target_id } : {}),
    duration_minutes: fields.duration_minutes ?? undefined,
    intensity: fields.intensity || undefined,
    activity_type: fields.activity_type || undefined,
  });
  const action = r?.action || 'not_found';
  const message = action === 'updated' ? 'Workout updated!'
    : action === 'unchanged' ? 'Found the workout but nothing to change.'
    : r?.message || 'No matching workout found — nothing was changed.';
  return { route: 'resolve-edit', action, message, candidates: r?.candidates };
}

// Correction aimed at a meeting note ("actually the meeting note should say we
// agreed on Q3") goes to POST /meetings/notes/resolve-edit. Only call when
// isCorrectionEdit(data) is true (or with target_id after the user picked a
// candidate).
export async function executeMeetingNoteCorrection(
  api: ApiLike,
  data: VoiceIntentData,
  transcript: string,
  opts: { targetId?: string } = {},
): Promise<CaptureCorrectionResult> {
  const { data: r } = await api.post('/meetings/notes/resolve-edit', {
    match: data.correction_target,
    ...(opts.targetId ? { target_id: opts.targetId } : {}),
    content: data.extracted_task || transcript,
  });
  const action = r?.action || 'not_found';
  const message = action === 'updated' ? 'Meeting note updated!'
    : r?.message || 'No matching meeting note found — nothing was changed.';
  return { route: 'resolve-edit', action, message, candidates: r?.candidates };
}

// Correction aimed at a note ("actually the note should say Q3") goes to
// POST /notes/resolve-edit. Only call when isCorrectionEdit(data) is true
// (or with target_id after the user picked a candidate).
export async function executeNoteCorrection(
  api: ApiLike,
  data: VoiceIntentData,
  transcript: string,
  opts: { targetId?: string } = {},
): Promise<CaptureCorrectionResult> {
  const { data: r } = await api.post('/notes/resolve-edit', {
    match: data.correction_target,
    ...(opts.targetId ? { target_id: opts.targetId } : {}),
    content: data.extracted_task || transcript,
  });
  const action = r?.action || 'not_found';
  const message = action === 'updated' ? 'Note updated!'
    : r?.message || 'No matching note found — nothing was changed.';
  return { route: 'resolve-edit', action, message, candidates: r?.candidates };
}
